import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useLoginFormStore from '../store/loginFormStore';
import useOptionConfig from '../hooks/useOptionConfig';
import useLoginStyles from '../hooks/useLoginStyles';
import { resolveInstitutionId } from '../api/api';
import { Box, Typography, Alert, IconButton, InputAdornment, Paper } from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import Input from './common/Input';
import Button from './common/Button';

const Login = () => {
  const { username, setUsername, password, setPassword, error, setError, reset, showPassword, toggleShowPassword } = useLoginFormStore();
  const { login, logout } = useAuthStore();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const institution = params.institution || searchParams.get('institution');
  const { config } = useOptionConfig();
  const styles = useLoginStyles();


  const getLogo = () => {
    const instId = resolveInstitutionId(institution);
    return config?.institutions?.[instId]?.logo || 
      (instId === '1' ? 'https://mexain.com/assets/mexa-logo-B3NTk3LZ.png' : '');
  };

  const logoUrl = getLogo();

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Get labels from config with fallbacks
  const labels = config?.labels?.login || {};
  const title = labels.title || 'Admin Login';
  const subtitle = labels.subtitle || 'Welcome back! Please sign in to continue.';
  const usernameLabel = labels.username || 'Username';
  const passwordLabel = labels.password || 'Password';
  const signInText = labels.signIn || 'Sign In';
  const permissionDenied = labels.permissionDenied || 'You do not have permission to access the admin dashboard.';
  const loginFailed = labels.loginFailed || 'Login failed. Please check your credentials and try again.';

  useEffect(() => {
    logout();
    setError(null);
    reset();
    return () => {
      reset();
    };
  }, [logout, reset, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError('Please enter both username and password.');
      return;
    }

    try {
      const { user } = await login({ username, password });

      if (user?.rank?.name === 'ADMIN' || user?.rank?.name === 'SUPERADMIN' || user?.rank?.name === 'STAFF') {
        navigate('/dashboard');
      } else {
        logout();
        setError(permissionDenied);
      }
    } catch (err) {
      setError(loginFailed);
      console.error('Login failed:', err);
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.card} elevation={0}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', visibility: logoUrl ? 'visible' : 'hidden' }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/assets/mexa-logo.png';
              }}
              style={{ width: '200px', height: 'auto' }}
            />
          )}
        </Box>
        <Typography component="h1" variant="h4" sx={styles.title}>
          {title}
        </Typography>
        <Typography variant="body2" sx={styles.subtitle}>
          {subtitle}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={styles.form}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Input
            sx={styles.input}
            required
            id="username"
            label={usernameLabel}
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: styles.iconColor }} />
                </InputAdornment>
              ),
            }}
          />
          <Input
            sx={styles.input}
            required
            name="password"
            label={passwordLabel}
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: styles.iconColor }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={toggleShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            sx={styles.button}
          >
            {signInText}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
